/**
 * This functional Component "AddMedia" define props like inital values and functions for child form component.
 * Created Date: 23-April-2024
 */

import { observer } from "mobx-react";
import React, { useEffect } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../../../contexts/store-provider";
import { initialState as AddFreeTextMediaState } from "../../../../core/initialState/add-free-text-media-state";
import { getAccessOrgIdFromLocalStorage, getTenantAccessFromLocalStorage } from "../../../../helpers/localstorage-helper";
import { formatMessage } from "../../../../translations/format-message";
import AddEditFreeTextForm from "../../forms/add-edit-media/add-edit-free-text-form";
import "./add-edit-media.css";

interface IProps {
  id: number;
  mediaType: number;
  selectedFolderType: number;
  modalClosed: () => void;
}
const AddEditMediaFreeText = (props: IProps) => {
  const { mediaStore, preferencesStore } = useStore();
  const {
    addUpdateMediaState,
    GetFreeTextMediaByIdService,
    mediaState,
    resetAddUpdateMediaState,
    resetImageMediaDetail,
    resetVideoMediaDetail,
    UpdateFreeTextMediaService,
    GetAllFolders,
    getAllFolderState,
    allAvailableFolders,
    addUpdateFreeTextMediaState,
    AddFreeTextMediaService,
    resetAddUpdateFreeTextMediaState,
    resetFreeTextMediaDetail,
    freeTextMediaDetail
  } = mediaStore;
  const { getMediaAccess } = preferencesStore;

  /**
   * The useEffect Hook lets you perform side effects in function component.
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered
   * while a dependency of the useEffect has changed.
   */
  useEffect(() => {
    GetAllFolders(getTenantAccessFromLocalStorage());
    if (props.id > 0) {
      GetFreeTextMediaByIdService(props.id, getAccessOrgIdFromLocalStorage());
    }
  }, []);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateUserSuccess" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateFreeTextMediaState.success) {
      if (props.id === -1) toast.success(formatMessage("added_success"));
      else toast.success(formatMessage("updated_success"));
      onModalClosed();
    }
  }, [addUpdateFreeTextMediaState.success]);

  /**
   * The function we pass to the useEffect Hook runs when the component is mounted and when it is re-rendered while
   * dependency "addUpdateMediaError" of the useEffect changed.
   */
  useEffect(() => {
    if (addUpdateFreeTextMediaState.error) {
      resetAddUpdateFreeTextMediaState();
      onModalClosed();
    }
  }, [addUpdateFreeTextMediaState.error]);

  /**
   * This function closes the pop up modal and reset getmediadetails, addUpdate media observables (success, inprogress
   * and error).
   */
  const onModalClosed = () => {
    resetFreeTextMediaDetail();
    resetAddUpdateFreeTextMediaState();
    props.modalClosed();
  };

  /**
   * This function add media when "Id < 0" and updates existing Image / Video when Id > 0 by providing form values to corresponding function
   */
  const onSubmitForm = (values: any) => {
      let obj = {
          OrganizationId: getAccessOrgIdFromLocalStorage()?.toLowerCase(),
          FolderId: getMediaAccess.folderId,
          FreeText: values.FreeText,
          FreeTextHtml: values.FreeTextHTML,
          FreeTextName: values.MediaName,
          Comment: values.Comment,
          Color: values.TextColor,
          FontSize: values.FontSize,
          FontType: values.FontFamily,
          IsScroll: values.IsScroll,
          Direction: values.Direction,
          Height: JSON.parse(values.Height),
          SpeedTime: JSON.parse(values.SpeedTime),
          ScrollWidth: 0,
        };

        if (props.id > 0) {
          UpdateFreeTextMediaService({
            FtId: props.id,
            FolderId: getMediaAccess.folderId,
            OrganizationId: getAccessOrgIdFromLocalStorage()?.toLowerCase(),
            FreeText: obj.FreeText,
            FreeTextHtml: obj.FreeTextHtml,
            Comment: obj.Comment,
            Color: obj.Color,
            FontSize: obj.FontSize,
            FontType: obj.FontType,
            IsScroll: obj.IsScroll,
            Direction: obj.Direction,
            Height: obj.Height,
            SpeedTime: obj.SpeedTime,
            ScrollWidth: 0,
          });
        } else {
          AddFreeTextMediaService(obj);
        }
  };

  return (
    <React.Fragment>
      <Modal
        className="correction-modal fancy-modal add-device-modal"
        size="lg"
        show={true}
        centered={true}
        onHide={() => {}}
      >
        <Modal.Header>
          <Modal.Title>
            {props.id > 0 ? (
              <>
                <FormattedMessage id="edit_free_text_media" />
              </>
            ) : (
              <>
                <FormattedMessage id="add_free_text_media" />
              </>
            )}
          </Modal.Title>
          <button
            onClick={onModalClosed}
            data-testid="Close"
            className="Crossicons"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="23"
              height="23"
              fill="currentColor"
              className="bi bi-x"
              viewBox="0 0 16 16"
            >
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body>
          <div className="pricing-value-modal">
            <AddEditFreeTextForm
              initialValues={freeTextMediaDetail}
              id={props.id}
              allFolders={allAvailableFolders}
              submitHandler={onSubmitForm}
              onModalClosed={onModalClosed}
              selectedFolderType={props.selectedFolderType}
              isLoading={
                mediaState.inProgress ||
                addUpdateFreeTextMediaState.inProgress ||
                getAllFolderState.inProgress
              }
            />
          </div>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};
export default observer(AddEditMediaFreeText);
